import { authorizedJurisdictionResolver } from './resolvers/authorized-jurisdiction.resolver';
import { classificationRatesResolver } from './master-wage-data/resolvers/classification-rates.resolver';
import { supportAdminSettingResolver } from './resolvers/admin-settings.resolver';
import { userCanGoBackGuard } from './master-wage-data/master-wage-rates-form/guards/cancel-confirmation.guard';
import { AppRoutes } from 'src/app/shared/interfaces/app-routes.interface';
import { Role } from 'src/app/shared/enums/role.enum';
import { RoleGuard } from 'src/app/shared/guards/role.guard';
import { masterWagesResolver } from './master-wage-data/manage-master-wage-data/resolvers/master-wages.resolver';
import { manageMasterWageRatesFormResolver } from './master-wage-data/master-wage-rates-form/resolvers/manage-master-wage-rates-form.resolver';
import { MasterWageRateFormService } from './master-wage-data/master-wage-rates-form/services/master-wage-rate-form.service';
import { MasterWageRateDataService } from './master-wage-data/master-wage-rates-form/services/master-wage-rate-data.service';
import { ClassificationRatesService } from './master-wage-data/services/classification-rates.service';
import { manageProjectWagesResolver } from './project-wage-data/project-wage-rate-form/resolvers/manage-project-wages.resolver';
import { manageProjectWagesGuard } from './project-wage-data/project-wage-rate-form/guards/manage-project-wages.guard';
import { projectCancelConfirmationGuard } from './project-wage-data/project-wage-rate-form/guards/project-cancel-confirmation.guard';
import { mergeResolvers } from 'src/app/core/utilities/merge-resolvers.util';
import { AssignMasterToProjectResolverResponse } from './master-wage-data/interfaces/assign-master-to-project.interface';
import { filterProjectsByJurisdictionResolver } from './master-wage-data/assign-to-project-wage-data/resolvers/filter-projects-by-jurisdiction.resolver';
import { clearFilterDataOnExitGuard } from './master-wage-data/manage-master-wage-data/guards/clear-filter-data-on-exit.guard';
import { Breadcrumb } from 'src/app/shared/components/navigation/breadcrumb/breadcrumb.const';

const redirectUrl = '/setup/prevailing-wage-setup/manage-master-wage-data';

/**
 * Define the routes for PrevailingWageSetupComponent
 * Export the routing provider for use in setup-routing.config.ts or any parent routing configuration
 */
export const PREVAILING_WAGE_SETUP_ROUTES: AppRoutes = [
  {
    path: 'crafts',
    canActivate: [RoleGuard(redirectUrl)],
    loadComponent: () =>
      import('./lcp-admin-features/crafts/crafts-list/crafts-list.component').then(
        c => c.CraftsListComponent
      ),
    data: { roles: [Role.SupportAdmin, Role.Admin, Role.BusinessManager] },
  },
  {
    path: 'manage-master-wage-data',
    canActivate: [RoleGuard(redirectUrl)],
    canDeactivate: [clearFilterDataOnExitGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./master-wage-data/manage-master-wage-data/manage-master-wage-data.component').then(
            c => c.ManageMasterWageDataComponent
          ),
        data: {
          breadcrumbs: [Breadcrumb.Home],
        },
      },
      {
        path: 'add',
        canDeactivate: [userCanGoBackGuard],
        canActivate: [RoleGuard(redirectUrl)],
        loadComponent: () =>
          import('./master-wage-data/master-wage-rates-form/master-wage-rates-form.component').then(
            c => c.MasterWageRatesFormComponent
          ),
        resolve: { masterWageData: manageMasterWageRatesFormResolver },
        providers: [
          MasterWageRateDataService,
          MasterWageRateFormService,
          ClassificationRatesService,
        ],
        data: {
          breadcrumbs: [
            Breadcrumb.Home,
            Breadcrumb.ManageMasterWagesDetermination,
          ],
          roles: [Role.Admin, Role.WageDataAdmin, Role.SupportAdmin],
        },
      },
      {
        path: 'view',
        children: [
          {
            path: 'classification-rate/add/:id/:jurisdictionId',
            canDeactivate: [userCanGoBackGuard],
            loadComponent: () =>
              import('./master-wage-data/master-wage-rates-form/master-wage-rates-form.component').then(
                c => c.MasterWageRatesFormComponent
              ),
            resolve: { masterWageData: manageMasterWageRatesFormResolver },
            providers: [
              MasterWageRateDataService,
              MasterWageRateFormService,
              ClassificationRatesService,
            ],
            data: {
              breadcrumbs: [
                Breadcrumb.Home,
                Breadcrumb.ManageMasterWagesDetermination,
              ],
            },
          },
          {
            path: 'classification-rate/edit/:id/:jurisdictionId',
            canDeactivate: [userCanGoBackGuard],
            loadComponent: () =>
              import('./master-wage-data/master-wage-rates-form/master-wage-rates-form.component').then(
                c => c.MasterWageRatesFormComponent
              ),
            resolve: { masterWageData: manageMasterWageRatesFormResolver },
            providers: [
              MasterWageRateDataService,
              MasterWageRateFormService,
              ClassificationRatesService,
            ],
            data: {
              breadcrumbs: [
                Breadcrumb.Home,
                Breadcrumb.ManageMasterWagesDetermination,
              ],
            },
          },
          {
            path: 'classification-rate/copy/:id/:jurisdictionId',
            canDeactivate: [userCanGoBackGuard],
            loadComponent: () =>
              import('./master-wage-data/master-wage-rates-form/master-wage-rates-form.component').then(
                c => c.MasterWageRatesFormComponent
              ),
            resolve: { masterWageData: manageMasterWageRatesFormResolver },
            providers: [
              MasterWageRateDataService,
              MasterWageRateFormService,
              ClassificationRatesService,
            ],
            data: {
              breadcrumbs: [
                Breadcrumb.Home,
                Breadcrumb.ManageMasterWagesDetermination,
              ],
            },
          },
          {
            path: ':wageDeterminationModificationId/:jurisdictionId/:wageLocationId/:constructionTypeId',
            resolve: {
              classificationRateResolverResponse: classificationRatesResolver,
              authorizedJurisdictionInfoResolverResponse:
                authorizedJurisdictionResolver,
            },
            loadComponent: () =>
              import('./master-wage-data/view-master-wage-rates/view-master-wage-rates.component').then(
                c => c.ViewMasterWageRatesComponent
              ),
            data: {
              breadcrumbs: [
                Breadcrumb.Home,
                Breadcrumb.ManageMasterWagesDetermination,
              ],
            },
          },
        ],
      },
      {
        path: 'assign-to-project-wage-data',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./master-wage-data/assign-to-project-wage-data/assign-to-project-wage-data.component').then(
                c => c.AssignToProjectWageDataComponent
              ),
            data: {
              breadcrumbs: [
                Breadcrumb.Home,
                Breadcrumb.ManageMasterWagesDetermination,
              ],
            },
          },
          {
            path: ':wageDeterminationModificationId/:jurisdictionId/:wageLocationId/:jurisdictionLocationId/:constructionTypeId',
            loadComponent: () =>
              import('./master-wage-data/assign-to-project-wage-data/assign-to-project-wage-data.component').then(
                c => c.AssignToProjectWageDataComponent
              ),
            resolve: {
              assignMasterToProjectResolverResponse:
                mergeResolvers<AssignMasterToProjectResolverResponse>({
                  classificationRatesResolverData: classificationRatesResolver,
                  masterWagesResolverData: masterWagesResolver,
                  projectsListByJurisdictionResolverData:
                    filterProjectsByJurisdictionResolver,
                }),
            },
            data: {
              breadcrumbs: [
                Breadcrumb.Home,
                Breadcrumb.ManageMasterWagesDetermination,
              ],
            },
          },
        ],
      },
      {
        path: 'manage-classification-rates',
        loadComponent: () =>
          import('./master-wage-data/manage-classification-rates/manage-classification-rates.component').then(
            c => c.ManageClassificationRatesComponent
          ),
        data: {
          breadcrumbs: [Breadcrumb.Home],
        },
      },
      {
        canActivate: [RoleGuard(redirectUrl)],
        path: 'copy-master-wage-data/:wageDeterminationModificationId/:jurisdictionId/:wageLocationId/:jurisdictionLocationId/:constructionTypeId',
        loadComponent: () =>
          import('./master-wage-data/copy-master-wage-data/copy-master-wage-data.component').then(
            c => c.CopyMasterWageDataComponent
          ),
        resolve: {
          classificationRateResolverResponse: classificationRatesResolver,
          masterWagesResolverResponse: masterWagesResolver,
        },
        data: {
          breadcrumbs: [
            Breadcrumb.Home,
            Breadcrumb.ManageMasterWagesDetermination,
          ],
          roles: [
            Role.Admin,
            Role.WageDataAdmin,
            Role.SupportAdmin,
            Role.FullAdmin,
          ],
        },
      },
    ],
    data: {
      roles: [
        Role.SupportAdmin,
        Role.WageDataAdmin,
        Role.Admin,
        Role.BusinessManager,
        Role.LimitedAdmin,
      ],
    },
  },
  {
    path: 'manage-project-wage-data',
    canActivate: [RoleGuard(redirectUrl)],
    runGuardsAndResolvers: 'always',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./project-wage-data/project-wage-data.component').then(
            c => c.ProjectWageDataComponent
          ),
        data: {
          breadcrumbs: [Breadcrumb.Home],
        },
      },
      {
        path: 'assign-to-project-wage-data',
        loadComponent: () =>
          import('./master-wage-data/assign-to-project-wage-data/assign-to-project-wage-data.component').then(
            c => c.AssignToProjectWageDataComponent
          ),
        data: {
          breadcrumbs: [Breadcrumb.Home, Breadcrumb.ManageProjectWages],
        },
      },
      {
        path: ':formMode/:legacyProjectId',
        canDeactivate: [projectCancelConfirmationGuard],
        loadComponent: () =>
          import('@setup/prevailing-wage-setup/project-wage-data/project-wage-rate-form/project-wage-rate-form.component').then(
            m => m.ProjectWageRateFormComponent
          ),
        canActivate: [manageProjectWagesGuard],
        resolve: {
          manageProjectWagesResolverResponse: manageProjectWagesResolver,
        },
        data: {
          breadcrumbs: [Breadcrumb.Home, Breadcrumb.ManageProjectWages],
        },
      },
      {
        path: ':formMode/:legacyProjectId/:classificationId',
        canDeactivate: [projectCancelConfirmationGuard],
        loadComponent: () =>
          import('@setup/prevailing-wage-setup/project-wage-data/project-wage-rate-form/project-wage-rate-form.component').then(
            m => m.ProjectWageRateFormComponent
          ),
        canActivate: [manageProjectWagesGuard],
        resolve: {
          manageProjectWagesResolverResponse: manageProjectWagesResolver,
        },
        data: {
          breadcrumbs: [Breadcrumb.Home, Breadcrumb.ManageProjectWages],
        },
      },
    ],
    data: {
      roles: [
        Role.SupportAdmin,
        Role.WageDataAdmin,
        Role.Admin,
        Role.LimitedAdmin,
        Role.BusinessManager,
      ],
    },
  },
  {
    path: 'minimum-wages',
    canActivate: [RoleGuard(redirectUrl)],
    loadComponent: () =>
      import('./manage-minimum-wage/manage-minimum-wage.component').then(
        c => c.ManageMinimumWageComponent
      ),
    data: { roles: [Role.SupportAdmin, Role.Admin, Role.BusinessManager] },
  },
  {
    path: 'settings',
    canActivate: [RoleGuard(redirectUrl)],
    loadComponent: () =>
      import('./lcp-admin-features/lcp-admin-features.component').then(
        c => c.LCPAdminFeaturesComponent
      ),
    data: { roles: [Role.SupportAdmin, Role.WageDataAdmin] },
    resolve: { supportAdminSetting: supportAdminSettingResolver },
  },
];
